import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircleIcon, XCircleIcon, ShieldCheckIcon, AlertTriangleIcon, FileTextIcon, VideoIcon, ClipboardListIcon, CalendarIcon } from 'lucide-react';
import { ServiceNowSubNav } from '../components/ServiceNowSubNav';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.5,
      ease: 'easeOut'
    }
  })
};
const syncPatterns = [{
  pattern: 'One-Way (Primary → Secure)',
  useCase: 'Organizational data, base user attributes',
  method: 'Removable media, Guard solution',
  frequency: 'Daily/Weekly'
}, {
  pattern: 'One-Way (Secure → Primary)',
  useCase: 'Aggregated metrics (sanitized)',
  method: 'Manual export after classification review',
  frequency: 'Monthly'
}, {
  pattern: 'No Sync',
  useCase: 'Operational data, mission context',
  method: 'Maintain separately',
  frequency: 'N/A'
}, {
  pattern: 'Bi-Directional (rare)',
  useCase: 'Non-sensitive config data',
  method: 'Complex approval process',
  frequency: 'As needed'
}];
export function SecureOnPremisePage() {
  return <div className="w-full" data-id="element-2487" data-mp="37oaza7471nyj">
      <ServiceNowSubNav data-id="element-2488" data-mp="37pck33b0hotg" />
      {/* HERO */}
      <section className="section-navy hero-grid-pattern" data-id="element-2489" data-mp="3cnxj4zej22gb">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15 text-center" data-id="element-2490" data-mp="3cne4brk6bu6i">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-2491" data-mp="2f1goy313c4ys">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-nrs-orange/20 border border-nrs-orange/40 rounded-full text-nrs-orange text-xs font-semibold uppercase tracking-wider mb-6" data-id="element-2492" data-mp="2oxbap70m2g5s">
              <ShieldCheckIcon className="h-3.5 w-3.5" data-id="element-2493" data-mp="472i7phais4v" />
              Cleared Team | Defense Specialists
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight max-w-4xl mx-auto" data-id="element-2494" data-mp="95nkdug3rlvy">
              Most ServiceNow Consultants Can't Touch Your Secure On-Premise
              Environment. <span className="text-nrs-orange" data-id="element-2495" data-mp="20ah16x53fnmp">We Can.</span>
            </h1>
            <div className="h-1.5 w-24 bg-nrs-orange mx-auto rounded-full mb-6" data-id="element-2496" data-mp="2ebbadhzytsmf"></div>
            <p className="text-xl text-nrs-light font-bold mb-8 max-w-3xl mx-auto" data-id="element-2497" data-mp="2a8wrwz07kuvo">
              For agencies operating ServiceNow across secure instances,
              cross-instance data synchronization is one of the most painful,
              risky, and expensive challenges.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2498" data-mp="1vcp7pyosvtge">
              <a href="mailto:info@newriversystems.com" className="btn-primary" data-id="element-2499" data-mp="1qlgwqy7to014">
                Schedule Secure Instance Assessment
              </a>
              <a href="https://drive.google.com/file/d/1J8g2FBnUX_0SU3OsQdAGsk0fzTrcJbak/view?usp=drive_link" target="_blank" rel="noopener noreferrer" className="btn-secondary" data-id="element-2500" data-mp="12ujukd5twh32">
                Download Governance Guide
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="section-white" data-id="element-2501" data-mp="tj5pjv2zvc2l">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-2502" data-mp="esokm1r5i40y">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-2503" data-mp="jr9dfg42plfd">
            <div data-id="element-2504" data-mp="jrj4l70utymn">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-nrs-navy mb-6" data-id="element-2505" data-mp="jowxt9lsfn5h">
                The Problem
              </h2>
              <p className="text-gray-600 text-sm mb-6" data-id="element-2506" data-mp="1cg6ujfwjtc6j">
                Most agencies operating ServiceNow in secure environments face:
              </p>
              <div className="space-y-3" data-id="element-2507" data-mp="xkqzp8o59c93">
                <div className="flex items-start gap-3 text-sm text-gray-700 bg-red-50 rounded-lg p-3 border border-red-100" data-id="element-2508" data-mp="12gy5n1gjinr1">
                  <XCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-2509" data-mp="3i7by9py3ybur" />
                  <div data-id="element-2510" data-mp="3n91dziw9zdp6">
                    <strong data-id="element-2511" data-mp="3n99qvaseej0d">Manual Data Replication</strong> — Admins manually
                    re-key data from primary to secure instances (high error
                    rate, labor intensive)
                  </div>
                </div>
                <div className="flex items-start gap-3 text-sm text-gray-700 bg-red-50 rounded-lg p-3 border border-red-100" data-id="element-2512" data-mp="3tp1o877we7f6">
                  <XCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-2513" data-mp="3or9ncnp9zayu" />
                  <div data-id="element-2514" data-mp="q8up8e0fgycq">
                    <strong data-id="element-2515" data-mp="q82qwvfg4mir">Data Divergence</strong> — Primary and secure
                    instances fall out of sync, causing operational confusion
                  </div>
                </div>
                <div className="flex items-start gap-3 text-sm text-gray-700 bg-red-50 rounded-lg p-3 border border-red-100" data-id="element-2516" data-mp="g93pnxx0ft5s">
                  <XCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-2517" data-mp="bdendmtlvy5s" />
                  <div data-id="element-2518" data-mp="311xgvvlc1h08">
                    <strong data-id="element-2519" data-mp="30y9pp33d16oo">Security Risks</strong> — Inconsistent data between
                    instances creates accidental spillage opportunities
                  </div>
                </div>
                <div className="flex items-start gap-3 text-sm text-gray-700 bg-red-50 rounded-lg p-3 border border-red-100" data-id="element-2520" data-mp="1bhnvwxjqx23p">
                  <XCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-2521" data-mp="1lh6d5689u9s2" />
                  <div data-id="element-2522" data-mp="1q84um94zxdww">
                    <strong data-id="element-2523" data-mp="1q8obdmiwtw9c">No Standard Patterns</strong> — Each agency
                    reinvents cross-instance sync (no best practices)
                  </div>
                </div>
                <div className="flex items-start gap-3 text-sm text-gray-700 bg-red-50 rounded-lg p-3 border border-red-100" data-id="element-2524" data-mp="1z8jtkbavuriv">
                  <XCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-2525" data-mp="249haylo2jhjb" />
                  <div data-id="element-2526" data-mp="297iq4vidiwpi">
                    <strong data-id="element-2527" data-mp="296qy1unzjh3z">Lack of Cleared Consultants</strong> — Most
                    ServiceNow SIs can't access secure environments to help
                  </div>
                </div>
              </div>
            </div>

            <div data-id="element-2528" data-mp="ev2q4cg68et3">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-nrs-navy mb-6" data-id="element-2529" data-mp="6sa7cugmwlgv">
                Why This Is Hard
              </h2>
              <div className="space-y-4" data-id="element-2530" data-mp="2sj9k6sox52u">
                <div className="border border-gray-200 rounded-lg p-4" data-id="element-2531" data-mp="gqq1l6aykaul">
                  <div className="font-heading font-semibold text-sm text-nrs-navy mb-1" data-id="element-2532" data-mp="gs10arpjqseu">
                    Air-Gapped Networks
                  </div>
                  <p className="text-gray-600 text-sm" data-id="element-2533" data-mp="pqmj8nyxk9g5">
                    Primary and secure instances cannot directly connect
                    (security by design)
                  </p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4" data-id="element-2534" data-mp="zg623tmn83b0">
                  <div className="font-heading font-semibold text-sm text-nrs-navy mb-1" data-id="element-2535" data-mp="uhmeloseijgj">
                    Classification Challenges
                  </div>
                  <p className="text-gray-600 text-sm" data-id="element-2536" data-mp="19dare2bscew1">
                    Determining what data CAN sync vs. what must stay
                    instance-specific
                  </p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4" data-id="element-2537" data-mp="1o09fpofcit6j">
                  <div className="font-heading font-semibold text-sm text-nrs-navy mb-1" data-id="element-2538" data-mp="1s35suzm49x33">
                    Data Governance Complexity
                  </div>
                  <p className="text-gray-600 text-sm" data-id="element-2539" data-mp="1j2hmmr2yt10r">
                    Master data ownership unclear across instances
                  </p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4" data-id="element-2540" data-mp="14ca46bcyji02">
                  <div className="font-heading font-semibold text-sm text-nrs-navy mb-1" data-id="element-2541" data-mp="196eaqtib9o0m">
                    RMF/ATO Impact
                  </div>
                  <p className="text-gray-600 text-sm" data-id="element-2542" data-mp="10c9okne4h0rf">
                    Cross-instance sync changes require re-authorization
                  </p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4" data-id="element-2543" data-mp="lhlc2pab122b">
                  <div className="font-heading font-semibold text-sm text-nrs-navy mb-1" data-id="element-2544" data-mp="qdtzsr2ki3l3">
                    Vendor Access
                  </div>
                  <p className="text-gray-600 text-sm" data-id="element-2545" data-mp="11v1aete06u7q">
                    Most consultants lack clearances to work in secure
                    environments
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW NRS SOLVES THIS */}
      <section className="section-light-blue" data-id="element-2546" data-mp="mxr1nrsqw2fp">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-2547" data-mp="mzlnr9fr60ld">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-2548" data-mp="i5z9xdxurqnz">
            <h2 className="text-3xl font-bold text-center mb-4" data-id="element-2549" data-mp="97xc179f1e4x">
              How New River Systems Solves This
            </h2>
            <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-6" data-id="element-2550" data-mp="3vdaoo92toomb"></div>
            <p className="text-gray-600" data-id="element-2551" data-mp="3vfn6mm8kyyzd">
              Our Cross-Instance Data Governance Framework
            </p>
          </motion.div>

          <div className="space-y-6" data-id="element-2552" data-mp="3q7ns5orb9sql">
            {[{
            step: '01',
            title: 'Classification Triage',
            desc: 'Identify which data domains MUST sync (users, organizational structure, CIs) and which CANNOT sync (operational details, mission-specific context). Create classification matrix for each ServiceNow table.'
          }, {
            step: '02',
            title: 'Master Data Ownership',
            desc: 'Designate primary or secure instance as "System of Record" for each data domain. Example: Primary is SoR for organizational structure; Secure is SoR for secure assets. Document in Data Governance Charter.'
          }, {
            step: '03',
            title: 'Sync Pattern Selection',
            desc: 'Based on classification and direction, choose the appropriate sync pattern. See the pattern matrix below.'
          }, {
            step: '04',
            title: 'Data Quality Monitoring',
            desc: 'Track sync success rate (target: >98%), monitor data discrepancy rate between instances (target: <2%), and alert on critical failures (user missing in secure instance but present in primary).'
          }, {
            step: '05',
            title: 'Security & ATO Integration',
            desc: 'Pre-approved sync patterns documented in RMF package. Change management process for new data types. Regular audits to prevent classification violations.'
          }].map((item, i) => <motion.div key={item.step} custom={i} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="bg-white rounded-xl p-6 flex gap-5 items-start border border-gray-100 shadow-sm" data-id="element-2553" data-mp="y77agz2w5u9f" data-mpi={i}>
                <div className="flex-shrink-0 w-12 h-12 bg-nrs-blue rounded-full flex items-center justify-center" data-id="element-2554" data-mp="ahwfxzc4ww3q">
                  <span className="text-white font-heading font-bold text-sm" data-id="element-2555" data-mp="ehwynw69ntgl">
                    {item.step}
                  </span>
                </div>
                <div data-id="element-2556" data-mp="20cnmfhmcqke">
                  <h3 className="font-heading font-bold text-base text-nrs-navy mb-2" data-id="element-2557" data-mp="1zuguoxbyty8">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed" data-id="element-2558" data-mp="3u2bxpvy9z6km">
                    {item.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>

          {/* Sync Pattern Table */}
          <div className="mt-10 bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-2559" data-mp="3k7tokba3aitf">
            <div className="bg-nrs-navy px-6 py-4" data-id="element-2560" data-mp="3ag5v64tz2rpb">
              <h3 className="font-heading font-bold text-white text-base" data-id="element-2561" data-mp="3agz153l3xd6e">
                Sync Pattern Matrix
              </h3>
            </div>
            <div className="overflow-x-auto" data-id="element-2562" data-mp="31fhoxwatlvmz">
              <table className="w-full text-sm" data-id="element-2563" data-mp="31e6cz9781hks">
                <thead className="bg-gray-50 border-b border-gray-200" data-id="element-2564" data-mp="2wgejiaqxhneh">
                  <tr data-id="element-2565" data-mp="2wh6dxi8shf36">
                    <th className="px-4 py-3 text-left font-heading font-semibold text-gray-700 text-xs uppercase" data-id="element-2566" data-mp="2mssitbm2ck7m">
                      Pattern
                    </th>
                    <th className="px-4 py-3 text-left font-heading font-semibold text-gray-700 text-xs uppercase" data-id="element-2567" data-mp="2rt6s0wzei078">
                      Use Case
                    </th>
                    <th className="px-4 py-3 text-left font-heading font-semibold text-gray-700 text-xs uppercase" data-id="element-2568" data-mp="2hur1ow876uys">
                      Method
                    </th>
                    <th className="px-4 py-3 text-left font-heading font-semibold text-gray-700 text-xs uppercase" data-id="element-2569" data-mp="3aiwkhdfoy9vz">
                      Frequency
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-2570" data-mp="30rs9eot4z96f">
                  {syncPatterns.map((row, i) => <tr key={row.pattern} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-id="element-2571" data-mp="30q7cwtn9hg5x" data-mpi={i}>
                      <td className="px-4 py-3 font-semibold text-nrs-navy text-xs" data-id="element-2572" data-mp="3ogbeywksx7ut">
                        {row.pattern}
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs" data-id="element-2573" data-mp="1hsu95t499rba">
                        {row.useCase}
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs" data-id="element-2574" data-mp="1csoowy0bf738">
                        {row.method}
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs" data-id="element-2575" data-mp="1mkuho9ye5sjh">
                        {row.frequency}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* COMPETITIVE ADVANTAGE */}
      <section className="section-navy" data-id="element-2576" data-mp="17w7p36jr74ab">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-2577" data-mp="17x9dsdtozpai">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-2578" data-mp="13tutvc2muors">
            <div data-id="element-2579" data-mp="2jalckaalhhpt">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-6" data-id="element-2580" data-mp="2ja3dl5wjspe7">
                New River Systems' Competitive Advantage
              </h2>
              <div className="space-y-4" data-id="element-2581" data-mp="3ajg3i5dskkhx">
                <div className="flex items-start gap-3" data-id="element-2582" data-mp="3akrg8up7t2l6">
                  <CheckCircleIcon className="h-5 w-5 text-nrs-orange flex-shrink-0 mt-0.5" data-id="element-2583" data-mp="3ekrtwd4hp9r6" />
                  <div data-id="element-2584" data-mp="3jk4hj036qkut">
                    <div className="text-white font-semibold text-sm" data-id="element-2585" data-mp="3jjv040w7hm0q">
                      Cleared Team
                    </div>
                    <div className="text-blue-200 text-xs mt-0.5" data-id="element-2586" data-mp="3oksfk5pu01yj">
                      We can actually access your secure environment
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-2587" data-mp="1corqug2tkdn">
                  <CheckCircleIcon className="h-5 w-5 text-nrs-orange flex-shrink-0 mt-0.5" data-id="element-2588" data-mp="64yczx68xw9l" />
                  <div data-id="element-2589" data-mp="b4uftt37jhnk">
                    <div className="text-white font-semibold text-sm" data-id="element-2590" data-mp="b2zvogzrftkj">
                      Defense Experience
                    </div>
                    <div className="text-blue-200 text-xs mt-0.5" data-id="element-2591" data-mp="g1kwoimu6ha1">
                      We've implemented cross-instance sync for multiple defense
                      agencies
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-2592" data-mp="20key5t1w8xqa">
                  <CheckCircleIcon className="h-5 w-5 text-nrs-orange flex-shrink-0 mt-0.5" data-id="element-2593" data-mp="20nsy6un34uuk" />
                  <div data-id="element-2594" data-mp="1vo6mwqqthooe">
                    <div className="text-white font-semibold text-sm" data-id="element-2595" data-mp="1qnseh7lb0cpu">
                      Proven Patterns
                    </div>
                    <div className="text-blue-200 text-xs mt-0.5" data-id="element-2596" data-mp="2o9pt2twkbxwi">
                      We have documented, ATO-approved sync architectures
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-2597" data-mp="2ejmxcly245uh">
                  <CheckCircleIcon className="h-5 w-5 text-nrs-orange flex-shrink-0 mt-0.5" data-id="element-2598" data-mp="29mo92k12qt47" />
                  <div data-id="element-2599" data-mp="37224051a87i9">
                    <div className="text-white font-semibold text-sm" data-id="element-2600" data-mp="372k5bg2sxbx1">
                      Data Governance Expertise
                    </div>
                    <div className="text-blue-200 text-xs mt-0.5" data-id="element-2601" data-mp="328z5dihne7v7">
                      We know how to establish System of Record and master data
                      ownership
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-2602" data-mp="qmf8xzlvcnn8">
                  <CheckCircleIcon className="h-5 w-5 text-nrs-orange flex-shrink-0 mt-0.5" data-id="element-2603" data-mp="vmjq7rulpmei" />
                  <div data-id="element-2604" data-mp="vk718uqfebry">
                    <div className="text-white font-semibold text-sm" data-id="element-2605" data-mp="hm8w8fxzvrp4">
                      RMF/ATO Fluency
                    </div>
                    <div className="text-blue-200 text-xs mt-0.5" data-id="element-2606" data-mp="lpx655a3mzdj">
                      We understand how cross-instance sync impacts your
                      security posture
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Case Study Snapshot */}
            <div className="bg-white/10 rounded-xl p-6 border border-white/20" data-id="element-2607" data-mp="220ixurdlnllf">
              <div className="text-nrs-orange font-heading font-bold text-xs uppercase tracking-wider mb-3" data-id="element-2608" data-mp="2716rlkt7qgjc">
                Case Study Snapshot
              </div>
              <h3 className="font-heading font-bold text-white text-lg mb-4" data-id="element-2609" data-mp="3mlfq4o1mvz7g">
                Large Defense Organization
              </h3>
              <div className="text-blue-200 text-xs mb-4" data-id="element-2610" data-mp="55hd4u3nd9r0">
                8,000 primary instance users + 3,000 secure instance users
              </div>

              <div className="space-y-3 mb-5" data-id="element-2611" data-mp="e41qdfqfur7u">
                <div className="font-heading font-semibold text-white text-xs uppercase tracking-wider" data-id="element-2612" data-mp="e6xnj1ugpbv8">
                  Challenge
                </div>
                <div className="space-y-1 text-sm text-blue-200" data-id="element-2613" data-mp="o3ah7zq0bppz">
                  <div data-id="element-2614" data-mp="j3xt7bnegclt">• 40 hours/week manual data replication</div>
                  <div data-id="element-2615" data-mp="j4pd3p7mwlw9">• 15% error rate causing operational issues</div>
                  <div data-id="element-2616" data-mp="2pvf2isd03orq">• No visibility into data divergence</div>
                  <div data-id="element-2617" data-mp="2kvkbmot86z6o">• ATO at risk due to undocumented sync</div>
                </div>
              </div>

              <div className="border-t border-white/20 pt-4" data-id="element-2618" data-mp="1z2n260n8o2fs">
                <div className="font-heading font-semibold text-white text-xs uppercase tracking-wider mb-3" data-id="element-2619" data-mp="1z00ve3869qym">
                  Results
                </div>
                <div className="grid grid-cols-2 gap-3" data-id="element-2620" data-mp="28szmpsv1zvlx">
                  <div className="bg-white/10 rounded-lg p-3 text-center" data-id="element-2621" data-mp="2dr1dz12mv18h">
                    <div className="text-nrs-orange font-heading font-bold text-xl" data-id="element-2622" data-mp="2dt42ths1uobm">
                      95%
                    </div>
                    <div className="text-blue-200 text-xs" data-id="element-2623" data-mp="1l240rcda3uml">
                      Reduction in manual effort
                    </div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 text-center" data-id="element-2624" data-mp="1q05intyyxnwh">
                    <div className="text-nrs-orange font-heading font-bold text-xl" data-id="element-2625" data-mp="1u3tmq2oe61cz">
                      &lt;1%
                    </div>
                    <div className="text-blue-200 text-xs" data-id="element-2626" data-mp="31iycjqh2nwrn">
                      Error rate (from 15%)
                    </div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 text-center" data-id="element-2627" data-mp="ttk85v5tisuo">
                    <div className="text-nrs-orange font-heading font-bold text-xl" data-id="element-2628" data-mp="psqmz9jrfs5h">
                      &lt;2%
                    </div>
                    <div className="text-blue-200 text-xs" data-id="element-2629" data-mp="l0z0pb7n08l5">
                      Data discrepancy rate
                    </div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 text-center" data-id="element-2630" data-mp="b4vrjledb9op">
                    <div className="text-nrs-orange font-heading font-bold text-xl" data-id="element-2631" data-mp="b1zvy3pzsx3f">
                      ✓ ATO
                    </div>
                    <div className="text-blue-200 text-xs" data-id="element-2632" data-mp="22wc2xj04dnv">
                      Approved with sync documented
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RESOURCES */}
      <section className="section-white" data-id="element-2633" data-mp="1ri2s4ggl3c3u">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-2634" data-mp="1rjdt68ina5r9">
          <h2 className="text-3xl font-bold text-center mb-4" data-id="element-2635" data-mp="2ks5ik5wdab1x">
            Dedicated Resources
          </h2>
          <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-12" data-id="element-2636" data-mp="2fvf05bx3b3dc"></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2637" data-mp="2b2m0v3ke4n8n">
            <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="border border-gray-200 rounded-xl p-6 hover:border-nrs-blue hover:shadow-md transition-all" data-id="element-2638" data-mp="264sry2rpdf4o">
              <FileTextIcon className="h-8 w-8 text-nrs-blue mb-4" data-id="element-2639" data-mp="1x21gw42qoldk" />
              <div className="font-heading font-bold text-sm text-nrs-navy mb-2" data-id="element-2640" data-mp="1s3q71tcg2avc">
                Governance Guide
              </div>
              <p className="text-gray-600 text-xs mb-4" data-id="element-2641" data-mp="1n79f0o24jccz">
                "The Three Pillars of ServiceNow Governance: A Federal Agency
                Field Guide" (7 pages)
              </p>
              <a href="https://drive.google.com/file/d/1J8g2FBnUX_0SU3OsQdAGsk0fzTrcJbak/view?usp=drive_link" target="_blank" rel="noopener noreferrer" className="text-nrs-orange font-semibold text-xs hover:underline" data-id="element-2642" data-mp="3hjzaha854tmh">
                Download Governance Guide →
              </a>
            </motion.div>
            <motion.div custom={1} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="border border-gray-200 rounded-xl p-6 hover:border-nrs-blue hover:shadow-md transition-all" data-id="element-2643" data-mp="2o5yum7h2b6c">
              <VideoIcon className="h-8 w-8 text-nrs-blue mb-4" data-id="element-2644" data-mp="3b2xw49amvtul" />
              <div className="font-heading font-bold text-sm text-nrs-navy mb-2" data-id="element-2645" data-mp="3b5aa6bd9tk2d">
                Webinar
              </div>
              <p className="text-gray-600 text-xs mb-4" data-id="element-2646" data-mp="3l13uigx8ehup">
                "Secure Instance Sync: Patterns, Pitfalls, and Best Practices"
                (60 minutes)
              </p>
              <a href="mailto:info@newriversystems.com?subject=Webinar Registration - Secure Instance Sync" className="text-nrs-orange font-semibold text-xs hover:underline" data-id="element-2647" data-mp="vee6hk4dtd6o">
                Register for Next Session →
              </a>
            </motion.div>
            <motion.div custom={2} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="border border-gray-200 rounded-xl p-6 hover:border-nrs-blue hover:shadow-md transition-all" data-id="element-2648" data-mp="2reeq7setifla">
              <ClipboardListIcon className="h-8 w-8 text-nrs-blue mb-4" data-id="element-2649" data-mp="2dnt3fsm8tli4" />
              <div className="font-heading font-bold text-sm text-nrs-navy mb-2" data-id="element-2650" data-mp="2dlp0pe02ysjf">
                Self-Assessment
              </div>
              <p className="text-gray-600 text-xs mb-4" data-id="element-2651" data-mp="2mfv1xlcr4rpr">
                "Is Your Cross-Instance Sync at Risk?" (10-question checklist)
              </p>
              <Link to="/resources/cross-instance-sync-checklist" className="text-nrs-orange font-semibold text-xs hover:underline" data-id="element-2652" data-mp="2ho3jjy3r1sap">
                Take the Checklist →
              </Link>
            </motion.div>
            <motion.div custom={3} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="border border-gray-200 rounded-xl p-6 hover:border-nrs-blue hover:shadow-md transition-all" data-id="element-2653" data-mp="1pw4fspkd9l7i">
              <CalendarIcon className="h-8 w-8 text-nrs-blue mb-4" data-id="element-2654" data-mp="15wvt01rf04w9" />
              <div className="font-heading font-bold text-sm text-nrs-navy mb-2" data-id="element-2655" data-mp="1av72uchpmfeh">
                Free Consultation
              </div>
              <p className="text-gray-600 text-xs mb-4" data-id="element-2656" data-mp="1yki3ltz289sm">
                Free 30-minute cross-instance governance assessment for defense
                agencies
              </p>
              <a href="mailto:info@newriversystems.com" className="text-nrs-orange font-semibold text-xs hover:underline" data-id="element-2657" data-mp="1on3qo4cci352">
                Schedule Now →
              </a>
            </motion.div>
          </div>

          <div className="mt-10 bg-nrs-light rounded-xl p-6 border border-nrs-blue/20 text-center" data-id="element-2658" data-mp="d75n2fzxs9b8">
            <p className="text-nrs-navy font-semibold text-base" data-id="element-2659" data-mp="i6s0apfrlpk1">
              Cross-instance data governance is a massive barrier to entry for
              most ServiceNow consultants. New River Systems' cleared workforce
              and defense experience make us one of the few firms that can
              actually help with this problem.
            </p>
            <p className="text-gray-600 text-sm mt-2" data-id="element-2660" data-mp="olij37f1i9cd">
              If you operate ServiceNow across secure on-premise instances, this
              capability alone justifies engaging New River Systems.
            </p>
          </div>
        </div>
      </section>
    </div>;
}